import type { RadarSignal, RadarWatch } from './types';

export type UpdatePeriod = 'today' | 'week';
export function validTime(value?: string | null) {
  const time = value ? Date.parse(value) : NaN;
  return Number.isFinite(time) ? time : null;
}
export function signalAnchor(watchId: string, signalId: string) {
  return `radar-signal-${watchId}-${signalId}`;
}
export function signalNeedsConfirmation(signal: RadarSignal) {
  return signal.feed_group === 'unconfirmed' || signal.source?.check_status === 'unconfirmed';
}
export function signalFeedGroup(signal: RadarSignal) {
  // Legacy "background" classification also includes undated developments. Only
  // an explicit presentation group can move a row out of the visible updates.
  return signal.feed_group || (signalNeedsConfirmation(signal) ? 'unconfirmed' : 'update');
}
function compareSignals(a: RadarSignal, b: RadarSignal) {
  return Number(signalNeedsConfirmation(a)) - Number(signalNeedsConfirmation(b))
    || (validTime(b.detected_at) || 0) - (validTime(a.detected_at) || 0);
}
export function signalFeed(signals: RadarSignal[] = []) {
  const seen = new Set<string>();
  const unique = signals.filter(signal => {
    if (seen.has(signal.id)) return false;
    seen.add(signal.id);
    return true;
  });
  return unique.sort(compareSignals);
}
// Calendar days in the viewer's timezone, not event dates or scheduler batches.
export function watchUpdates(watches: RadarWatch[], period: UpdatePeriod, now = new Date()) {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(midnight);
  if (period === 'week') start.setDate(start.getDate() - 6);
  const current = watches.filter(w => w.status === 'active');
  const inRange = (value: string | null | undefined, since: number) => {
    const time = validTime(value);
    return time !== null && time >= since && time <= now.getTime();
  };
  const entries: {watch: RadarWatch; signal: RadarSignal}[] = [];
  for (const watch of current) {
    for (const signal of signalFeed(watch.signals)) {
      if (signalFeedGroup(signal) === 'background' || signal.historical_plan || signal.feedback === 'not_relevant'
        || !inRange(signal.detected_at, start.getTime())) continue;
      entries.push({watch, signal});
    }
  }
  entries.sort((a,b) => compareSignals(a.signal, b.signal));
  const latest = current.map(w => w.last_checked_at).filter(v => inRange(v, 0))
    .sort((a,b) => validTime(b)! - validTime(a)!)[0] || null;
  return {
    entries,
    unconfirmed: entries.filter(e => signalNeedsConfirmation(e.signal)).length,
    checkedToday: current.filter(w => ['completed','partial','no_change'].includes(w.check_state || '')
      && inRange(w.last_checked_at, midnight.getTime())).length,
    failed: current.filter(w => w.check_state === 'failed').length,
    running: current.filter(w => w.check_state === 'running').length,
    due: current.filter(w => w.check_state !== 'running' && inRange(w.next_check_at, 0)).length,
    latestCheck: latest,
  };
}
